import React from 'react';
import {
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { DailyScreenViews } from '../models/Statistics';

interface Props {
    data: DailyScreenViews;
    period: 'last7days' | 'last30days';
}

const UserTrafficChart: React.FC<Props> = ({ data, period }) => {
    const { t } = useTranslation();

    const currentData = data[period];
    const prevKey = period === 'last7days' ? 'prev7days' : 'prev30days';
    const prevData = data[prevKey] ?? [];

    // Align by day index so the two lines overlap on the same x-axis
    const maxLen = Math.max(currentData.length, prevData.length);
    const chartData = Array.from({ length: maxLen }, (_, i) => {
        const curr = currentData[i];
        const prev = prevData[i];
        return {
            day: i + 1,
            date: curr?.date ?? prev?.date ?? '',
            current: curr ? curr.count : null,
            previous: prev ? prev.count : null,
            prevDate: prev?.date,
        };
    });

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        const curr = payload.find((p: any) => p.dataKey === 'current');
        const prev = payload.find((p: any) => p.dataKey === 'previous');
        return (
            <div className="bg-white border border-neutral rounded p-2 text-sm shadow">
                {curr && (
                    <div>
                        <span className="font-semibold" style={{ color: '#1A3B40' }}>{curr.value?.toLocaleString()}</span>
                        <span className="text-utility-mutedText ml-1">— {curr.payload.date}</span>
                    </div>
                )}
                {prev && prev.value !== null && (
                    <div>
                        <span className="font-semibold" style={{ color: '#4399A6' }}>{prev.value?.toLocaleString()}</span>
                        <span className="text-utility-mutedText ml-1">— {prev.payload.prevDate}</span>
                    </div>
                )}
            </div>
        );
    };

    const hasPrev = prevData.length > 0;

    return (
        <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0eeef" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 12 }} width={40} />
                <Tooltip content={<CustomTooltip />} />
                {hasPrev && <Legend />}
                <Line
                    type="monotone"
                    dataKey="current"
                    name={t('statistics.current_period')}
                    stroke="#1A3B40"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls
                />
                {hasPrev && (
                    <Line
                        type="monotone"
                        dataKey="previous"
                        name={t('statistics.previous_period')}
                        stroke="#4399A6"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls
                    />
                )}
            </LineChart>
        </ResponsiveContainer>
    );
};

export default UserTrafficChart;
